import React, { useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { DataGrid } from "@mui/x-data-grid";
import { Button, CircularProgress, InputAdornment, TextField } from "@mui/material";
import SearchIcon from "@material-ui/icons/Search";
import EditIcon from "@material-ui/icons/Edit";
import { useAssignedForms } from "../../hooks/useAssignedForms";
import { useNavigate } from "react-router-dom";
import useDocFormsStore from "../../store/docFormsStore";
import FormsListEmptyState from "../FormsListEmptyState/FormsListEmptyState";
import MagicLinkInboxBanner, { inboxDataGridSx } from "../MagicLinkInboxBanner/MagicLinkInboxBanner";
import {
	isMagicLinkSession,
	getMagicLinkHighlightFormRequestId,
	getMagicLinkHighlightedFormName,
	getMagicLinkViewPath,
} from "Services/docForm.service";

const useStyles = makeStyles(() => ({
	container: {
		backgroundColor: "#fff",
		padding: "14px 16px",
		height: "100%",
		flex: 1,
		display: "flex",
		flexDirection: "column",
	},
	toolbar: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		marginBottom: 12,
	},
	searchInput: {
		flex: 1,
		maxWidth: 320,
	},
	docCell: {
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		lineHeight: 1.3,
		padding: "4px 0",
	},
	docName: {
		fontWeight: 500,
		fontSize: 14,
		color: "#111827",
	},
	docSub: {
		fontSize: 12,
		color: "#6b7280",
		marginTop: 2,
	},
	statusBadge: {
		display: "inline-flex",
		alignItems: "center",
		gap: 5,
		fontSize: 13,
		fontWeight: 500,
		color: "#92400e",
	},
	statusDot: {
		width: 7,
		height: 7,
		borderRadius: "50%",
		backgroundColor: "#f59e0b",
		flexShrink: 0,
	},
	openBtn: {
		textTransform: "none",
		fontSize: 13,
		padding: "4px 14px",
		borderRadius: 6,
	},
}));

export default function AssignedView({ setLoading, onViewCompleted }) {
	const classes = useStyles();
	const { assignedForms } = useAssignedForms();
	const inboxLoading = useDocFormsStore((state) => state.loading);
	const navigate = useNavigate();
	const highlightId = getMagicLinkHighlightFormRequestId();
	const isMagicLink = isMagicLinkSession();
	const [search, setSearch] = useState("");

	const allRows = useMemo(
		() =>
			(assignedForms || []).map((form, index) => ({
				...form,
				id: form.id || `assigned-${index}`,
			})),
		[assignedForms]
	);

	const rows = useMemo(() => {
		if (!search.trim()) return allRows;
		const q = search.toLowerCase();
		return allRows.filter(
			(r) =>
				(r.templateName || "").toLowerCase().includes(q) ||
				(r.senderName || "").toLowerCase().includes(q) ||
				(r.senderEmail || "").toLowerCase().includes(q)
		);
	}, [allRows, search]);

	const highlightedName = getMagicLinkHighlightedFormName(allRows);
	const viewPath = getMagicLinkViewPath();

	const toComplete = (row) => {
		setLoading(true, "Loading...");
		navigate(`/docFormFiller/${row.templateId}/${row.id}`);
	};

	const columns = [
		{
			field: "templateName",
			headerName: "Document",
			flex: 2,
			renderCell: (params) => {
				const { templateName, senderName, pageCount } = params.row;
				const sub = [senderName ? `From ${senderName}` : null, pageCount ? `${pageCount} page${pageCount !== 1 ? "s" : ""}` : null]
					.filter(Boolean)
					.join(" · ");
				return (
					<div className={classes.docCell}>
						<span className={classes.docName}>{templateName || "—"}</span>
						{sub && <span className={classes.docSub}>{sub}</span>}
					</div>
				);
			},
		},
		{
			field: "requestedAt",
			headerName: "Requested",
			flex: 1,
			valueGetter: (params) =>
				params.row.requestedAtDisplay || params.row.requestedAt || "—",
		},
		{
			field: "status",
			headerName: "Status",
			flex: 0.9,
			renderCell: () => (
				<span className={classes.statusBadge}>
					<span className={classes.statusDot} />
					Pending
				</span>
			),
		},
		{
			field: "toComplete",
			headerName: "Actions",
			flex: 0.8,
			sortable: false,
			renderCell: (params) => {
				const isHighlighted =
					highlightId && String(params.row.id) === String(highlightId);
				return (
					<Button
						variant={isHighlighted ? "contained" : "outlined"}
						size="small"
						className={classes.openBtn}
						startIcon={<EditIcon style={{ fontSize: 15 }} />}
						onClick={() => toComplete(params.row)}
						sx={isHighlighted ? { backgroundColor: "#006ac6", "&:hover": { backgroundColor: "#0056a3" } } : { borderColor: "#006ac6", color: "#006ac6" }}
					>
						Open
					</Button>
				);
			},
		},
	];

	if (inboxLoading) {
		return (
			<div
				className={classes.container}
				style={{ alignItems: "center", justifyContent: "center" }}
			>
				<CircularProgress color="primary" />
			</div>
		);
	}

	if (!allRows.length) {
		return (
			<FormsListEmptyState
				variant="assigned"
				onPrimaryAction={onViewCompleted}
				onSecondaryAction={() => {}}
			/>
		);
	}

	return (
		<div className={classes.container}>
			{isMagicLink && highlightId && (
				<MagicLinkInboxBanner
					formName={highlightedName || "Your form"}
					onOpenForm={viewPath ? () => navigate(viewPath) : undefined}
					actionLabel="Open this form"
					tab="Assigned"
				/>
			)}
			<div className={classes.toolbar}>
				<TextField
					variant="outlined"
					size="small"
					placeholder="Search forms..."
					className={classes.searchInput}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon style={{ color: "#9ca3af", fontSize: 20 }} />
							</InputAdornment>
						),
					}}
				/>
			</div>
			<DataGrid
				rows={rows}
				columns={columns}
				pageSize={10}
				rowsPerPageOptions={[10]}
				disableSelectionOnClick
				autoHeight
				rowHeight={56}
				getRowClassName={(params) =>
					highlightId && String(params.id) === String(highlightId)
						? "magic-link-highlight-row"
						: ""
				}
				sx={inboxDataGridSx}
			/>
		</div>
	);
}
